import { Component, OnInit, signal, computed, input, inject, DestroyRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { catchError, finalize, Observable, of, debounceTime } from 'rxjs';
import { CategoryItem, NameEntity } from '@models';
import { FormControlExtended, formControl } from '@al00x/forms';
import { CacButtonComponent, CacControlErrorComponent, CacFieldComponent } from '@controladad/ng-base';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface FlatCategoryNode {
  expandable: boolean;
  id: number;
  name: NameEntity;
  productCount: number;
  priority?: number;
  level: number;
  parentId?: number;
}

@Component({
  selector: 'app-category-tree',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    FormsModule,
    MatProgressBarModule,
    CacButtonComponent,
    AsyncPipe,
    MatCheckbox,
    CacControlErrorComponent,
    CacFieldComponent,
    NgIf,
  ],
  templateUrl: './category-tree.component.html',
  styleUrl: './category-tree.component.scss',
  standalone: true,
})
export class CategoryTreeComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  categories = input.required<Observable<CategoryItem[]>>();
  hideClear = input(true);
  label = input<string>();
  control = input.required<FormControlExtended>();
  multiple = input<boolean>(false);
  allowParentSelection = input<boolean>(true);
  isSearchable = input<boolean>(false);
  showPosition = input<boolean>(false);

  allNodes = signal<FlatCategoryNode[]>([]);
  expandedNodeIds = signal<Set<number>>(new Set<number>());
  loading = signal<boolean>(false);
  searchControl = formControl<string>('');
  searchTerm = signal<string>('');

  visibleNodes = computed(() => {
    const nodes = this.allNodes();
    const expandedIds = this.expandedNodeIds();
    const searchTerm = this.searchTerm().toLowerCase().trim();

    // If searching, filter and show matching nodes plus their parents
    if (searchTerm && this.isSearchable()) {
      const matchingNodes = nodes.filter((node) => {
        const englishName = node.name.english?.toLowerCase().trim() || '';
        const arabicName = node.name.arabic?.toLowerCase().trim() || '';

        // Normalize Arabic text for better matching
        const normalizedArabicName = arabicName.normalize('NFD');
        const normalizedSearchTerm = searchTerm.normalize('NFD');

        return englishName.includes(searchTerm) ||
               arabicName.includes(searchTerm) ||
               normalizedArabicName.includes(normalizedSearchTerm);
      });

      // Include parent nodes of matching results
      const visibleNodeIds = new Set<number>();

      matchingNodes.forEach(node => {
        visibleNodeIds.add(node.id);
        // Add all parent nodes
        const parentIds = this.findAllParentIds(node.id);
        parentIds.forEach(id => visibleNodeIds.add(id));
      });

      return nodes.filter(node => visibleNodeIds.has(node.id));
    }

    // Normal visibility filter when not searching
    return nodes.filter((node) => {
      if (node.level === 0) {
        return true;
      }

      let isVisible = true;
      let currentLevel = node.level;
      let currentNodeIndex = nodes.indexOf(node);

      while (currentLevel > 0 && isVisible) {
        let parentFound = false;
        for (let i = currentNodeIndex - 1; i >= 0; i--) {
          const potentialParent = nodes[i];
          if (potentialParent.level === currentLevel - 1) {
            if (!expandedIds.has(potentialParent.id)) {
              isVisible = false;
            }
            parentFound = true;
            currentLevel--;
            currentNodeIndex = i;
            break;
          }
        }

        if (!parentFound) {
          isVisible = false;
          break;
        }
      }

      return isVisible;
    });
  });

  ngOnInit() {
    this.loadCategories();

    this.control()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.expandParentsOfSelectedCategories());

    // Setup search listener with debounce (always setup, will only be used when searchable)
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        this.searchTerm.set(value || '');
      });
  }

  clear() {
    this.control().patchValue(null);
    this.control().setDisplayText('');
  }

  loadCategories() {
    this.loading.set(true);

    this.categories()
      .pipe(
        catchError((error) => {
          console.error('Error loading categories:', error);
          return of([]);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((categories) => {
        this.allNodes.set(this.flattenTree(categories));
        this.expandParentsOfSelectedCategories();
      });
  }

  toggleNode(node: FlatCategoryNode): void {
    const expandedIds = new Set(this.expandedNodeIds());

    if (expandedIds.has(node.id)) {
      expandedIds.delete(node.id);
    } else {
      expandedIds.add(node.id);
    }

    this.expandedNodeIds.set(expandedIds);
  }

  isExpanded(nodeId: number): boolean {
    return this.expandedNodeIds().has(nodeId);
  }

  private flattenTree(data: CategoryItem[], level = 0, parentId?: number): FlatCategoryNode[] {
    let result: FlatCategoryNode[] = [];

    data.forEach((item) => {
      const flatNode: FlatCategoryNode = {
        id: item.id,
        name: item.name,
        productCount: item.productCount,
        priority: item.priority,
        expandable: !!item.subCategories && item.subCategories.length > 0,
        level,
        parentId,
      };

      result.push(flatNode);

      if (item.subCategories && item.subCategories.length > 0) {
        result = result.concat(this.flattenTree(item.subCategories, level + 1, item.id));
      }
    });

    return result;
  }

  selectCategory(id: number) {
    let newValue;
    if (this.multiple()) {
      const values: number[] = this.control().getRawValue() ?? [];
      newValue = values.includes(id) ? values.filter((i) => i !== id) : [...values, id];
    } else {
      newValue = id;
    }
    this.control().setValue(newValue);
  }

  private expandParentsOfSelectedCategories(): void {
    const selectedCategories = this.getSelectedCategoryIds();
    if (selectedCategories.length === 0) return;

    const expandedIds = new Set(this.expandedNodeIds());

    selectedCategories.forEach((categoryId) => {
      const parentIds = this.findAllParentIds(categoryId);
      parentIds.forEach((id) => expandedIds.add(id));
    });

    this.expandedNodeIds.set(expandedIds);
  }

  private getSelectedCategoryIds(): number[] {
    const value = this.control().getRawValue();
    if (!value) return [];

    if (this.multiple()) {
      return Array.isArray(value) ? value : [];
    } else {
      return typeof value === 'number' ? [value] : [];
    }
  }

  private findAllParentIds(categoryId: number): number[] {
    const parentIds: number[] = [];
    const nodes = this.allNodes();

    let currentNode = nodes.find((node) => node.id === categoryId);
    while (currentNode && currentNode.parentId !== undefined) {
      parentIds.push(currentNode.parentId);
      currentNode = nodes.find((node) => node.id === currentNode?.parentId);
    }

    return parentIds;
  }

  shouldShowPriority(node: FlatCategoryNode): boolean {
    return this.showPosition() && node.priority !== 999;
  }

  shouldShowNodeCount(node: FlatCategoryNode): boolean {
    return node.productCount > 0;
  }
}
